import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { pnl as fmtPnl, tone } from "@/lib/format";
import { cn } from "./core";

export function Stat({ label, value, sub, valueClass, icon, className }: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  valueClass?: string;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className="flex items-center gap-1.5 text-[10.5px] font-medium tracking-wide text-muted uppercase sm:text-[11.5px]">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className={cn("mt-1 truncate text-[18px] leading-tight font-semibold tracking-tight num sm:text-[20px]", valueClass)}>{value}</div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-muted sm:text-[11.5px]">{sub}</div>}
    </div>
  );
}

/** Signed rupees, coloured, with a brief flash when the value moves. */
export function Pnl({ value, decimals = 0, className, flash = true }: {
  value: number | null | undefined;
  decimals?: 0 | 2;
  className?: string;
  flash?: boolean;
}) {
  const prev = useRef(value);
  const [cls, setCls] = useState("");
  useEffect(() => {
    if (!flash || value == null || prev.current == null || value === prev.current) {
      prev.current = value;
      return;
    }
    setCls(value > prev.current ? "flash-up" : "flash-down");
    prev.current = value;
    const t = window.setTimeout(() => setCls(""), 900);
    return () => window.clearTimeout(t);
  }, [value, flash]);
  const t = tone(value);
  return (
    <span className={cn("num rounded px-0.5", t === "up" && "text-up", t === "down" && "text-down", cls, className)}>
      {fmtPnl(value, decimals)}
    </span>
  );
}

export function Meter({ value, max, tone: t = "accent", className, label }: {
  value: number;
  max: number;
  tone?: "accent" | "up" | "down" | "warn";
  className?: string;
  label?: string;
}) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const color = { accent: "bg-accent", up: "bg-up", down: "bg-down", warn: "bg-warn" }[pct >= 85 ? "down" : pct >= 60 ? "warn" : t];
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-panel-3", className)} role="meter" aria-valuenow={value} aria-valuemax={max} aria-label={label}>
      <div className={cn("h-full rounded-full transition-[width] duration-500", color)} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ── tables ──────────────────────────────────────────────────────────────────
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("w-full overflow-x-auto", className)}>
      <table className="w-full border-collapse text-left text-[12.5px]">{children}</table>
    </div>
  );
}

export function TH({ children, className, right, onClick, sorted }: {
  children?: ReactNode;
  className?: string;
  right?: boolean;
  onClick?: () => void;
  sorted?: "asc" | "desc" | false;
}) {
  return (
    <th
      onClick={onClick}
      className={cn(
        "sticky top-0 z-[1] h-8 border-b border-line bg-panel px-3 text-[11px] font-medium tracking-wide whitespace-nowrap text-muted uppercase",
        right && "text-right",
        onClick && "cursor-pointer select-none hover:text-fg",
        className,
      )}
    >
      {children}
      {sorted === "asc" && <ChevronUp className="ml-0.5 inline size-3 text-faint" />}
      {sorted === "desc" && <ChevronDown className="ml-0.5 inline size-3 text-faint" />}
    </th>
  );
}

export function TD({ children, className, right, mono }: { children?: ReactNode; className?: string; right?: boolean; mono?: boolean }) {
  return <td className={cn("h-9 border-b border-line/60 px-3 whitespace-nowrap", right && "text-right", mono && "num", className)}>{children}</td>;
}

export function TR({ children, className, onClick, active }: { children: ReactNode; className?: string; onClick?: () => void; active?: boolean }) {
  return (
    <tr onClick={onClick} className={cn("transition-colors", onClick && "cursor-pointer hover:bg-panel-2", active && "bg-panel-2", className)}>
      {children}
    </tr>
  );
}

// ── phone lists: what a table becomes below desktop width (pair with a table shown from lg) ──
export function MobileList({ children, className }: { children: ReactNode; className?: string }) {
  return <ul className={cn("divide-y divide-line lg:hidden", className)}>{children}</ul>;
}

/** One row: badges, a title and a detail line on the left; the number that
 * matters on the right; a chevron when it opens something. */
export function MobileRow({ title, subtitle, badges, value, valueSub, onClick, leading, trailing, className }: {
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  value?: ReactNode;
  valueSub?: ReactNode;
  onClick?: () => void;
  leading?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  const body = (
    <>
      {leading}
      <div className="min-w-0 flex-1">
        {badges && <div className="mb-1 flex flex-wrap items-center gap-1">{badges}</div>}
        <div className="truncate text-[14px] font-medium">{title}</div>
        {subtitle && <div className="mt-0.5 truncate text-[12px] text-muted">{subtitle}</div>}
      </div>
      {(value !== undefined || valueSub !== undefined) && (
        <div className="shrink-0 text-right">
          {value !== undefined && <div className="text-[14px] font-semibold num">{value}</div>}
          {valueSub !== undefined && <div className="mt-0.5 text-[11.5px] text-muted num">{valueSub}</div>}
        </div>
      )}
      {trailing}
      {onClick && !trailing && <ChevronRight className="size-4 shrink-0 text-faint" />}
    </>
  );
  return (
    <li className={className}>
      {onClick ? (
        <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()} className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left active:bg-panel-2">
          {body}
        </div>
      ) : (
        <div className="flex w-full items-center gap-3 px-4 py-3">{body}</div>
      )}
    </li>
  );
}

// ── tabs ────────────────────────────────────────────────────────────────────
export function Tabs({ value, onChange, tabs, children, className, listClassName }: {
  value: string;
  onChange: (v: string) => void;
  tabs: { value: string; label: ReactNode; count?: number }[];
  children?: ReactNode;
  className?: string;
  listClassName?: string;
}) {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onChange} className={className}>
      <TabsPrimitive.List className={cn("flex gap-1 overflow-x-auto border-b border-line", listClassName)}>
        {tabs.map((t) => (
          <TabsPrimitive.Trigger
            key={t.value}
            value={t.value}
            className="relative -mb-px flex h-9 items-center gap-1.5 border-b-2 border-transparent px-3 text-[12.5px] font-medium whitespace-nowrap text-muted transition-colors hover:text-fg data-[state=active]:border-accent data-[state=active]:text-fg"
          >
            {t.label}
            {t.count !== undefined && <span className="rounded bg-panel-3 px-1.5 text-[10.5px] text-muted num">{t.count}</span>}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {children}
    </TabsPrimitive.Root>
  );
}

export const TabPanel = TabsPrimitive.Content;

export function KV({ items, className }: { items: [ReactNode, ReactNode][]; className?: string }) {
  return (
    <dl className={cn("grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[12.5px]", className)}>
      {items.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd className="min-w-0 truncate text-right num">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
